package com.schoolconnect.app.feature.reports

import com.schoolconnect.app.datasource.network.response.UsageAdoptionPagination
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.pageCountForTotal
import com.schoolconnect.app.repository.reports.ReportsRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.intOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** Usage & Adoption report presenter backed by ReportsRepository. */
class ReportsViewModel {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Main.immediate)
    private var loadJob: Job? = null
    private var exportJob: Job? = null
    private val tag = "[UI] ReportsViewModel"

    private val _uiState = MutableStateFlow(ReportsUiState())
    val uiState: StateFlow<ReportsUiState> = _uiState.asStateFlow()

    private val _apiRows = MutableStateFlow<List<UsageAdoptionRowUi>>(emptyList())
    private var useApiData = false
    private var lastApiTotal = 0

    private val demoRows = listOf(
        UsageAdoptionRowUi("School Admin", 4, 4, 4, 18.5, 0),
        UsageAdoptionRowUi("Teacher", 42, 38, 38, 12.4, 2),
        UsageAdoptionRowUi("Parent", 1280, 968, 968, 6.8, 146)
    )

    fun onEvent(event: ReportsUiEvent) {
        when (event) {
            is ReportsUiEvent.FromDateChanged -> _uiState.update { it.copy(fromDate = event.value, validationMessage = "") }
            is ReportsUiEvent.ToDateChanged -> _uiState.update { it.copy(toDate = event.value, validationMessage = "") }
            is ReportsUiEvent.RoleChanged -> _uiState.update { it.copy(role = event.value, feedbackMessage = "") }
            is ReportsUiEvent.MetricChanged -> _uiState.update { it.copy(metric = event.value, feedbackMessage = "") }
            is ReportsUiEvent.OperatorChanged -> _uiState.update { it.copy(operator = event.value, feedbackMessage = "") }
            is ReportsUiEvent.FirstValueChanged -> _uiState.update { it.copy(firstValue = event.value.filter(Char::isDigit).take(3), validationMessage = "") }
            is ReportsUiEvent.SecondValueChanged -> _uiState.update { it.copy(secondValue = event.value.filter(Char::isDigit).take(3), validationMessage = "") }
            ReportsUiEvent.RunReport -> runReport()
            ReportsUiEvent.Reset -> {
                useApiData = false
                _apiRows.value = emptyList()
                lastApiTotal = 0
                _uiState.value = ReportsUiState()
            }
            ReportsUiEvent.ViewFullReport -> {
                _uiState.update { it.copy(showFullReport = true, pageIndex = 0, feedbackMessage = "") }
                if (useApiData) {
                    fetchPage(0)
                }
            }
            ReportsUiEvent.ShowPreview -> _uiState.update { it.copy(showFullReport = false, feedbackMessage = "") }
            is ReportsUiEvent.ExportSelected -> exportReport(event.format)
            is ReportsUiEvent.PageChanged -> {
                val state = _uiState.value
                if (useApiData && state.showFullReport) {
                    val count = pageCountForTotal(state.totalCount, state.pageSize)
                    val newPage = event.pageIndex.coerceIn(0, count - 1)
                    _uiState.update { it.copy(pageIndex = newPage) }
                    if (newPage != state.pageIndex) {
                        fetchPage(newPage)
                    }
                } else {
                    _uiState.update { s ->
                        val count = pageCountForTotal(filteredRows(s).size, s.pageSize)
                        s.copy(pageIndex = event.pageIndex.coerceIn(0, count - 1))
                    }
                }
            }
            is ReportsUiEvent.PageSizeChanged -> {
                _uiState.update { state ->
                    state.copy(pageSize = event.pageSize.coerceAtLeast(1), pageIndex = 0, rowsPerPageExpanded = false)
                }
                if (useApiData && _uiState.value.showFullReport) {
                    fetchPage(0)
                }
            }
            ReportsUiEvent.ToggleRowsPerPage -> _uiState.update { it.copy(rowsPerPageExpanded = !it.rowsPerPageExpanded) }
            ReportsUiEvent.DismissRowsPerPage -> _uiState.update { it.copy(rowsPerPageExpanded = false) }
        }
    }

    fun resultRows(state: ReportsUiState = _uiState.value): List<UsageAdoptionRowUi> = filteredRows(state)

    fun clear() {
        loadJob?.cancel()
        exportJob?.cancel()
        scope.cancel()
    }

    private fun filteredRows(state: ReportsUiState): List<UsageAdoptionRowUi> {
        val source = if (useApiData) _apiRows.value else demoRows
        return if (useApiData && state.showFullReport) {
            source
        } else if (useApiData) {
            source.filter { row ->
                val matchesRole = state.appliedRole == "All Roles" || row.role == state.appliedRole
                matchesRole
            }
        } else {
            source.filter { row ->
                val matchesRole = state.appliedRole == "All Roles" || row.role == state.appliedRole
                val rate = row.adoptionRate
                val matchesMetric = state.appliedMetric == "None" || when (state.appliedOperator) {
                    "Greater than" -> rate != null && state.appliedFirstValue.toDoubleOrNull()?.let { rate > it } == true
                    "Equal to" -> rate != null && state.appliedFirstValue.toDoubleOrNull()?.let { rate == it } == true
                    "Between" -> {
                        val lower = state.appliedFirstValue.toDoubleOrNull()
                        val upper = state.appliedSecondValue.toDoubleOrNull()
                        rate != null && lower != null && upper != null && rate in lower..upper
                    }
                    else -> rate != null && state.appliedFirstValue.toDoubleOrNull()?.let { rate < it } == true
                }
                matchesRole && matchesMetric
            }
        }
    }

    private fun runReport() {
        val current = _uiState.value
        if (current.fromDate.isBlank() || current.toDate.isBlank()) {
            _uiState.update { it.copy(validationMessage = "Choose both dates to run the report.") }
            return
        }
        if (current.metric != "None") {
            val first = current.firstValue.toIntOrNull()
            val second = current.secondValue.toIntOrNull()
            if (first == null || first !in 0..100 || (current.operator == "Between" && (second == null || second !in 0..100))) {
                _uiState.update { it.copy(validationMessage = "Enter percentage values from 0 to 100.") }
                return
            }
            if (current.operator == "Between" && second!! < first) {
                _uiState.update { it.copy(validationMessage = "The second percentage must be greater than or equal to the first.") }
                return
            }
        }

        val fromApi = toApiDate(current.fromDate)
        val toApi = toApiDate(current.toDate)
        val role = if (current.role == "All Roles") "All" else current.role
        val values = if (current.metric == "Adoption Rate") {
            when (current.operator) {
                "Between" -> listOf((current.firstValue.toDoubleOrNull() ?: 0.0), (current.secondValue.toDoubleOrNull() ?: 0.0))
                else -> listOf(current.firstValue.toDoubleOrNull() ?: 0.0)
            }
        } else null

        loadJob?.cancel()
        loadJob = scope.launch {
            scLog("$tag runReport() called - role=$role from=$fromApi to=$toApi metric=${current.metric} pageSize=${current.pageSize}")
            _uiState.update { it.copy(validationMessage = "", feedbackMessage = "Loading report...", isLoading = true) }
            val result = ReportsRepository.getUsageAdoptionReport(
                fromDate = fromApi,
                toDate = toApi,
                role = role,
                criteriaType = if (current.metric == "Adoption Rate") "Adoption Rate" else null,
                operator = if (current.metric == "Adoption Rate") current.operator else null,
                values = values,
                limit = current.pageSize,
                offset = 0
            )
            result.onSuccess { response ->
                scLog("$tag API success: rows=${response.rows.size}, pagination=$response.pagination")
                val rows = response.rows.map { row ->
                    UsageAdoptionRowUi(
                        role = row.role,
                        totalUsers = row.totalUsers,
                        activeUsers = row.activeUsers,
                        adoptedUsers = row.adoptedUsers,
                        averageLoginsPerUser = row.averageLoginsPerUser,
                        neverLoggedIn = row.neverLoggedIn
                    )
                }
                _apiRows.value = rows
                useApiData = true
                val total = extractTotal(response.pagination)
                lastApiTotal = total
                _uiState.update {
                    it.copy(
                        appliedFromDate = it.fromDate,
                        appliedToDate = it.toDate,
                        appliedRole = it.role,
                        appliedMetric = it.metric,
                        appliedOperator = it.operator,
                        appliedFirstValue = it.firstValue,
                        appliedSecondValue = it.secondValue,
                        validationMessage = "",
                        feedbackMessage = "Report loaded from API.",
                        showFullReport = false,
                        pageIndex = 0,
                        pageSize = it.pageSize,
                        totalCount = if (total == 0) rows.size else total,
                        isLoading = false
                    )
                }
            }.onFailure { error ->
                scLog("$tag runReport failed: ${error.message}")
                scLog("$tag stacktrace: ${error.stackTraceToString().take(200)}")
                useApiData = false
                _uiState.update {
                    it.copy(
                        appliedFromDate = it.fromDate,
                        appliedToDate = it.toDate,
                        appliedRole = it.role,
                        appliedMetric = it.metric,
                        appliedOperator = it.operator,
                        appliedFirstValue = it.firstValue,
                        appliedSecondValue = it.secondValue,
                        validationMessage = "",
                        feedbackMessage = "API unavailable, showing demo data.",
                        showFullReport = false,
                        pageIndex = 0,
                        pageSize = PaginationPolicy.DEFAULT_PAGE_SIZE,
                        totalCount = 0,
                        isLoading = false
                    )
                }
            }
        }
    }

    private fun fetchPage(pageIndex: Int) {
        val current = _uiState.value
        if (!useApiData) return
        val fromApi = toApiDate(current.appliedFromDate)
        val toApi = toApiDate(current.appliedToDate)
        val role = if (current.appliedRole == "All Roles") "All" else current.appliedRole
        val values = if (current.appliedMetric == "Adoption Rate") {
            when (current.appliedOperator) {
                "Between" -> listOf((current.appliedFirstValue.toDoubleOrNull() ?: 0.0), (current.appliedSecondValue.toDoubleOrNull() ?: 0.0))
                else -> listOf(current.appliedFirstValue.toDoubleOrNull() ?: 0.0)
            }
        } else null
        loadJob?.cancel()
        loadJob = scope.launch {
            _uiState.update { it.copy(isLoading = true, feedbackMessage = "Loading page...") }
            val offset = pageIndex * current.pageSize
            val result = ReportsRepository.getUsageAdoptionReport(
                fromDate = fromApi,
                toDate = toApi,
                role = role,
                criteriaType = if (current.appliedMetric == "Adoption Rate") "Adoption Rate" else null,
                operator = if (current.appliedMetric == "Adoption Rate") current.appliedOperator else null,
                values = values,
                limit = current.pageSize,
                offset = offset
            )
            result.onSuccess { response ->
                val rows = response.rows.map { row ->
                    UsageAdoptionRowUi(
                        role = row.role,
                        totalUsers = row.totalUsers,
                        activeUsers = row.activeUsers,
                        adoptedUsers = row.adoptedUsers,
                        averageLoginsPerUser = row.averageLoginsPerUser,
                        neverLoggedIn = row.neverLoggedIn
                    )
                }
                _apiRows.value = rows
                val total = extractTotal(response.pagination)
                if (total > 0) lastApiTotal = total
                _uiState.update {
                    it.copy(
                        pageIndex = pageIndex,
                        totalCount = if (total > 0) total else it.totalCount,
                        feedbackMessage = "Page loaded.",
                        isLoading = false
                    )
                }
            }.onFailure { error ->
                scLog("$tag fetchPage failed: ${error.message}")
                _uiState.update { it.copy(feedbackMessage = "Failed to load page.", isLoading = false) }
            }
        }
    }

    private fun extractTotal(pagination: kotlinx.serialization.json.JsonElement?): Int {
        if (pagination == null) return 0
        return try {
            val obj = pagination.jsonObject
            obj["total"]?.jsonPrimitive?.intOrNull
                ?: obj["total_count"]?.jsonPrimitive?.intOrNull
                ?: obj["count"]?.jsonPrimitive?.intOrNull
                ?: 0
        } catch (e: Exception) {
            0
        }
    }

    private fun exportReport(format: String) {
        val current = _uiState.value
        if (current.fromDate.isBlank() || current.toDate.isBlank()) {
            _uiState.update { it.copy(feedbackMessage = "Choose both dates to export the report.") }
            return
        }
        val fromApi = toApiDate(current.fromDate)
        val toApi = toApiDate(current.toDate)
        val role = if (current.appliedRole == "All Roles") "All" else current.appliedRole
        val values = if (current.appliedMetric == "Adoption Rate") {
            when (current.appliedOperator) {
                "Between" -> listOf((current.appliedFirstValue.toDoubleOrNull() ?: 0.0), (current.appliedSecondValue.toDoubleOrNull() ?: 0.0))
                else -> listOf(current.appliedFirstValue.toDoubleOrNull() ?: 0.0)
            }
        } else null
        exportJob?.cancel()
        exportJob = scope.launch {
            _uiState.update { it.copy(feedbackMessage = "Exporting $format...") }
            val result = ReportsRepository.exportUsageAdoptionReport(
                fromDate = fromApi,
                toDate = toApi,
                role = role,
                criteriaType = if (current.appliedMetric == "Adoption Rate") "Adoption Rate" else null,
                operator = if (current.appliedMetric == "Adoption Rate") current.appliedOperator else null,
                values = values,
                format = if (format == "Excel") "excel" else "pdf"
            )
            result.onSuccess {
                _uiState.update { it.copy(feedbackMessage = "$format export downloaded.") }
            }.onFailure { error ->
                scLog("$tag exportReport failed: ${error.message}")
                _uiState.update { it.copy(feedbackMessage = "Demo ${format} export is ready to connect; no file was generated.") }
            }
        }
    }

    private fun toApiDate(value: String): String {
        val parts = value.trim().split("\\s+".toRegex())
        if (parts.size != 3) return value
        val day = parts[0].padStart(2, '0')
        val month = when (parts[1].lowercase()) {
            "jan" -> "01"
            "feb" -> "02"
            "mar" -> "03"
            "apr" -> "04"
            "may" -> "05"
            "jun" -> "06"
            "jul" -> "07"
            "aug" -> "08"
            "sep" -> "09"
            "oct" -> "10"
            "nov" -> "11"
            "dec" -> "12"
            else -> "01"
        }
        val year = parts[2]
        return "$year-$month-$day"
    }
}
